import { runInAction } from 'mobx'
import { CancellablePromise } from 'mobx/dist/api/flow'
import _ from 'lodash'
import fp from 'lodash/fp'
import {
  addMilliseconds,
  differenceInMilliseconds,
  parseISO,
} from 'date-fns/fp'
import { get } from 'common/src/vtj/browser/http'
import { handleError } from 'todistukset-ui/src/vtj/stores/actions/common-actions'
import { createAction, createFlow } from 'common/src/vtj/mobx/action-creator'
import { getStore } from 'todistukset-ui/src/vtj/stores/store.util'
import { TodistuksetUi } from 'todistukset-ui/src/vtj/common-types'
import { queuePriorityDialogSessionAboutToExpire } from 'todistukset-ui/src/vtj/stores/actions/dialog-actions'
import { AxiosError } from 'axios'

const TEN_SECONDS_MS = 10 * 1000
const ONE_MINUTE_MS = 60 * 1000
const FIVE_MINUTES_MS = 5 * ONE_MINUTE_MS

const STATUS_REQUEST_THROTTLE_MS = TEN_SECONDS_MS

const timer: {
  countdownToExpireDialog?: number
  debounceTimeMs?: number
  debouncedRefreshSession?: TodistuksetUi.Store.DebouncedRefreshSession
  maxWait?: number
  sessionSecondWindTimeMs?: number
  lastStatusRequest?: number
  lastStatusRequestResult?: { sessionExpiresAt: string }
} = {}

export const updateSessionTimerStore = (
  updatedValues: TodistuksetUi.Store.InitSessionTimer
): void => {
  const store = getStore()
  const path = 'common.sessionTimer'
  const sessionTimer = { ..._.get(store, path), ...updatedValues }
  runInAction(() => _.set(store, path, sessionTimer))
}

export const clearCountdownToExpireDialog = (): void =>
  window.clearTimeout(timer.countdownToExpireDialog)

const flushInitialState = () => {
  ;(
    window as unknown as {
      INITIAL_STATE: unknown
    }
  ).INITIAL_STATE = null
}

export const handleSessionError = ({
  error,
}: {
  error?: Error | AxiosError
} = {}): void => {
  flushInitialState()
  clearCountdownToExpireDialog()
  const storePath = 'common.sessionExpiresAt'
  const errorValue = undefined
  handleError({
    error: (error as AxiosError)?.response || error,
    storePath,
    errorValue,
  })
}

export const getSessionExpiresInMs = ({
  sessionExpiresAt,
}: {
  sessionExpiresAt: string
}): {
  sessionExpiresInMs: number
} => {
  /**
   * Adding a second will give the session more time to go stale, thus it is
   * less probable that the client will make an extra session status check.
   */
  const now = new Date()
  const sessionExpiresInMs = fp.flow([
    parseISO,
    addMilliseconds(1000),
    differenceInMilliseconds(now),
  ])(sessionExpiresAt)

  return { sessionExpiresInMs }
}

const getTimeToSessionAboutToExpireDialogMs = ({
  sessionExpiresAt,
}: {
  sessionExpiresAt: string
}): { timeToSessionAboutToExpireDialogMs: number } => {
  const { sessionSecondWindTimeMs } = timer
  if (typeof sessionSecondWindTimeMs !== 'number') {
    throw new Error('sessionSecondWindTimeMs must be a number.')
  }

  const { sessionExpiresInMs } = getSessionExpiresInMs({ sessionExpiresAt })
  const timeToSessionAboutToExpireDialogMs = Math.max(
    0,
    sessionExpiresInMs - sessionSecondWindTimeMs
  )

  return { timeToSessionAboutToExpireDialogMs }
}

const requestSessionStatus = async (): Promise<{
  sessionExpiresAt: string
}> => {
  const now = Date.now()
  if (
    timer.lastStatusRequest &&
    timer.lastStatusRequestResult &&
    now < timer.lastStatusRequest + STATUS_REQUEST_THROTTLE_MS
  ) {
    return timer.lastStatusRequestResult
  }
  const [error, response] = await get('/api/restricted/session/status')
  if (error) {
    throw error
  }

  const sessionExpiresAt = fp.get('data.sessionExpiresAt', response)
  if (!sessionExpiresAt) {
    throw new Error('Something went wrong while fetching sessionExpiresAt.')
  }
  timer.lastStatusRequest = now
  timer.lastStatusRequestResult = { sessionExpiresAt }
  return { sessionExpiresAt }
}

export const refreshSessionTimerCountdown = async ({
  sessionExpiresAt,
  isExpireDialogAlreadyVisible: expireDialogVisible,
}: {
  sessionExpiresAt?: string
  isExpireDialogAlreadyVisible?: boolean
} = {}): Promise<void> => {
  clearCountdownToExpireDialog()

  try {
    sessionExpiresAt =
      sessionExpiresAt ?? (await requestSessionStatus()).sessionExpiresAt
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    handleSessionError({ error })
    return
  }

  const { timeToSessionAboutToExpireDialogMs } =
    getTimeToSessionAboutToExpireDialogMs({
      sessionExpiresAt,
    })

  const showSessionAboutToExpireDialog = !(
    timeToSessionAboutToExpireDialogMs || expireDialogVisible
  )

  updateSessionTimerStore({
    sessionExpiresAt,
  })

  if (showSessionAboutToExpireDialog) {
    void queuePriorityDialogSessionAboutToExpire()
  } else {
    timer.countdownToExpireDialog = window.setTimeout(() => {
      void refreshSessionTimerCountdown({
        isExpireDialogAlreadyVisible: expireDialogVisible,
      })
      // Schedule with at least a second apart to protect from busy
      // loop when server and client disagree about time
    }, Math.max(timeToSessionAboutToExpireDialogMs, 1000))
  }
}

const requestSessionRefresh = async (): Promise<void> => {
  try {
    const [error, response] = await get('/api/restricted/session/refresh')
    if (error) {
      throw error
    }
    const sessionExpiresAt = fp.get('data.sessionExpiresAt', response)
    if (!sessionExpiresAt) {
      throw new Error('Something went wrong with session validation.')
    }
    timer.lastStatusRequest = undefined
    timer.lastStatusRequestResult = undefined
    void refreshSessionTimerCountdown({ sessionExpiresAt })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    handleSessionError({ error })
  }
}

// @private, but we need the export for testing
export const initSessionTimer = ({
  sessionMaxAge,
}: {
  sessionMaxAge: number
}): void => {
  if (!sessionMaxAge || typeof sessionMaxAge !== 'number') {
    throw new Error(
      'session.maxAge is falsy or not a number, session timer cannot start.'
    )
  }
  const debounceTimeMs = Math.min(TEN_SECONDS_MS, sessionMaxAge / 30)
  const maxWait = Math.min(ONE_MINUTE_MS, sessionMaxAge / 5)
  timer.sessionSecondWindTimeMs = Math.min(FIVE_MINUTES_MS, sessionMaxAge / 6)
  timer.debounceTimeMs = debounceTimeMs
  timer.maxWait = maxWait
  timer.debouncedRefreshSession = _.debounce(
    requestSessionRefresh,
    debounceTimeMs,
    {
      leading: true,
      maxWait,
    }
  )
  timer.lastStatusRequest = undefined
  timer.lastStatusRequestResult = undefined
}

export const startSessionTimer = ({
  settings,
}: {
  settings: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
}): CancellablePromise<Function> => // eslint-disable-line @typescript-eslint/ban-types
  createFlow(function* ({
    common: {
      user,
      sessionTimer: { sessionExpiresAt },
    },
  }: TodistuksetUi.Store) {
    const sessionMaxAge = settings.get('session.maxAge')
    try {
      if (user && sessionExpiresAt) {
        initSessionTimer({ sessionMaxAge })
        void refreshSessionTimerCountdown({ sessionExpiresAt })
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      handleSessionError({ error })
    }
  })

export const refreshSession = ({ flushDebounce = false } = {}): void => {
  const {
    common: {
      user,
      sessionTimer: { sessionExpiresAt },
    },
  } = getStore()
  if (!user || !sessionExpiresAt) {
    return
  }
  const { debouncedRefreshSession } = timer
  if (typeof debouncedRefreshSession !== 'function') {
    throw new Error('debouncedRefreshSession must be a function.')
  }
  debouncedRefreshSession()
  if (flushDebounce) {
    debouncedRefreshSession.flush()
  }
}

export const refreshSessionNow = (): void => {
  refreshSession({ flushDebounce: true })
}

// eslint-disable-next-line @typescript-eslint/ban-types
const withRefreshSession = (fn: Function): Function => {
  refreshSession()
  return fn
}

export const createActionWithRefreshSession = (
  fn: Function // eslint-disable-line @typescript-eslint/ban-types
): CancellablePromise<Function> => createAction(withRefreshSession(fn)) // eslint-disable-line @typescript-eslint/ban-types
